import React, { useEffect, useState } from 'react';

import { downloadReport, fetchTravelExpenses, generateReport } from 'actions/expense';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { TravelExpensesResponse } from 'models/expense';
import ExpensesList from 'screens/components/ExpensesList';
import { extractErrorMessage } from 'utils/generic';

interface Params {
  match: {
    params: {
      travelId: string;
    };
  };
}

const ExpensesListContainer = ({ match: { params } }: Params) => {
  const toast = useToast();
  const { t } = useStoreTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [isReportDownloadLoading, setIsReportDownloadLoading] = useState(false);
  const [travel, setTravel] = useState<TravelExpensesResponse>();

  const handleFetchExpenses = () => {
    fetchTravelExpenses(params.travelId).then(({ data: { data } }) => {
      setTravel(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (params.travelId) {
      handleFetchExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateReport = async () => {
    try {
      setIsReportLoading(true);
      await generateReport(params.travelId);
      toast.add({ type: 'success', message: t('expensesList.generateReportSuccess') });
    } catch (error) {
      toast.add({ type: 'danger', message: extractErrorMessage(error) });
    } finally {
      setIsReportLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      setIsReportDownloadLoading(true);
      const data = await downloadReport(params.travelId);
      handleDownloadReportFile(data);
      toast.add({ type: 'success', message: t('expensesList.downloadReportSuccess') });
    } catch (error) {
      toast.add({ type: 'danger', message: extractErrorMessage(error) });
    } finally {
      setIsReportDownloadLoading(false);
    }
  };

  const handleDownloadReportFile = (data) => {
    const blob = new Blob([data.data], { type: 'application/zip' });
    const fileName = `${params.travelId}.zip`;

    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, fileName);
    } else {
      const link = document.createElement('a');
      // Browsers that support HTML5 download attribute
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  return (
    <ExpensesList
      isLoading={isLoading}
      isReportExportLoading={isReportLoading}
      travelId={params.travelId}
      travel={travel}
      onGenerateReport={handleGenerateReport}
      onDownloadReport={handleDownloadReport}
      isReportDownloadLoading={isReportDownloadLoading}
    />
  );
};

export default ExpensesListContainer;
